import React, { Component } from "react";
import { Switch, Route } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { API_URL } from "src/scenes/App";
import { getCompanySubscriptionEnd } from "src/utils/dates";
import {
    BusinessPortalHeader,
    LottieLoadingIndicator,
    PortalPageShell,
    PortalSideNav,
    PortalSideNavHeader,
    PortalSideNavIcon,
    PortalSideNavLink,
    PortalSideNavList,
    PortalSideNavText,
    PortalSurface,
    PortalWorkspace,
    Toast,
} from "src/components";
import MyBusiness from "./forms/MyBusiness";
import Payments from "./forms/Payments";
import BillingHistory from "./forms/BillingHistory";
import Security from "./forms/Security";
import CancelSubscription from "./forms/SubscriptionPage";
import RenewalAndEarning from "./forms/RenewalAndEarning";
import PrivacyAndData from "./forms/PrivacyAndData";
import MyLicense from "./forms/MyLicense";
import StorageManagement from "./forms/StorageManagement";
import { SettingContext } from "./SettingContext";
import { UserContext } from "src/scenes/App/UserContext";
import * as PERMISSIONS from "src/constants/permissions";
import { IsFeatureReady } from "src/constants/features";
import { SubscriptionMode, UserKeys } from "src/constants/userDetails";
import { SubscriptionStatus } from "src/components/SubscriptionElements";

import MyBusinessIcon from "../../img/subscription/my business.svg";
import SecurityIcon from "../../img/subscription/security.svg";
import BillingHistoryIcon from "../../img/subscription/billing history.svg";
import SubscriptionIcon from "../../img/subscription/subscription.svg";
import PaymentMethodsIcon from "../../img/subscription/credit-card.svg";
import StorageManagementIcon from "../../img/Storage.png";
import MyLicenseIcon from "../../img/license-user.png";
import RenewalsAndEarningIcon from "../../img/subscription/reneals and earnings.svg";
import DataPrivacy from "../../img/subscription/privacy and data.svg";

export const Forms = {
    MY_BUSINESS: "/setting/business",
    PAYMENTS: "/setting/payment",
    SECURITY: "/setting/security",
    BILLING_HISTORY: "/setting/billingHistory",
    SUBSCRIPTION: "/setting/subscriptions",
    STORAGE_MANAGEMENT: "/setting/storageManagement",
    RENEWALS_AND_EARNINGS: "/setting/renewalsAndEarnings",
    PRIVACY_AND_DATA: "/setting/privacyAndData",
    MY_LICENSE: "/setting/myLicense"
};

const TextDiv = styled.div`
  font-family: Manrope, Helvetica, sans-serif;
  font-size: ${ p => p.fontSize || "14px"};
  text-align: ${ p => p.textAlign || "left" };
  color: #11141A;
  flex-grow: ${ p => p.flexGrow };
  font-weight: ${ p => p.fontWeight || 400};
`;

const SettingsCustomerBadge = styled.div`
    min-width: 13rem;
    display: grid;
    gap: 0.15rem;
    padding: 0.85rem 1rem;
    background: #F3F5FB;
    border: 1px solid #D8DEEC;
    border-radius: 8px;
    box-sizing: border-box;
    text-align: right;

    @media (max-width: 900px) {
        text-align: left;
    }
`;

const SettingsContentPanel = styled(PortalSurface)`
    width: 100%;
    max-width: ${p => p.$compact ? "58rem" : "none"};
    justify-self: start;
    padding: 1.25rem;
    display: grid;
    align-items: ${p => p.$loading ? "center" : "start"};
    justify-items: ${p => p.$loading ? "center" : "stretch"};

    @media (max-width: 760px) {
        padding: 1rem;
        min-height: 0;
    }
`;

const ErrorAlert = styled.div`
    height: auto;
    padding: 0.8rem 2rem;
    width: 100%;
    max-width: 800px;
    background: #FFBABA;
    color: #D8000C;
    font-weight: 300;
    border-radius: 10px;
    border: 1px solid #D8000C;

`;


const planLabelFrom = (sub) => {
    if (!sub) return "No Subscription";

    const n = Number(sub.durationNumber ?? 0);
    const u = String(sub.durationUnit ?? "").toLowerCase();

    if (u === "month") return n === 12 ? "Yearly Subscription" : "Monthly Subscription";
    if (u === "day" && n === 7) return "Weekly Subscription";
    if (u === "hour" && n === 3) return "Hourly Subscription";

    return "Subscription";
};

const SettingsNavIconImage = styled.img`
    && {
        filter: ${p => p.$active
        ? "brightness(0) invert(1)"
        : p.$darkInactive
            ? "brightness(0) saturate(100%)"
            : "none"};
    }
`;

const SettingsNavItem = ({ to, active, icon, label, darkInactive }) => (
    <PortalSideNavLink to={to} $active={active}>
        <PortalSideNavIcon $active={active}>
            <SettingsNavIconImage src={icon} alt="" $active={active} $darkInactive={darkInactive} />
        </PortalSideNavIcon>
        <PortalSideNavText>{label}</PortalSideNavText>
    </PortalSideNavLink>
);

class Setting extends Component {
    static contextType = UserContext;

    state = {
        fetching: 0,
        countryCallingCode: "",
        //My Business
        website: null,
        //CancelSubscription
        subscriptionStart: null,
        subscriptionEnd: null,
        subscriptionNextPaymentDate: null,
        subscriptionPeriod: null,
        subscriptionPaymentPeriod: null,
        subscriptionStatus: null,
        hasActiveSubscription: false,
        employees: 0,
        clients: 0,
        inDemoMode: false,
        subscriptionDurationUnit: null,
        subscriptionTitle: "Subscription",
        emailPro: null,
    };

    componentDidMount() {
        this.safeToInit();
    }

    safeToInit = () => {
        if (this.context[UserKeys.IS_CUSTOMIZE_COMPLETE] && this.hasBillingPermission() && this.hasSalesAndPromotionsPermission()) {
            /**
             * running this function on accounts that are yet to finish their theme set up will show the RevokedPermissionsModal
             * need to finish setup to get the SalesAndPromotionAuthorization permission required for the API
             */
            this.getCompanyClientLicense();
        }

        this.getCompanyDetails();

        if (!this.context[UserKeys.IS_CLIENT]) {
            this.getCountryCallingCode();
        }
    };

    isAccountOwner = () => PERMISSIONS.hasPermissions([PERMISSIONS.ACCOUNT_OWNER_PERMISSION], this.context.permissions);

    hasBillingPermission = () => PERMISSIONS.hasPermissions([PERMISSIONS.BILLING], this.context.permissions);
    hasStorageManagementAccess = () => !this.context[UserKeys.IS_CLIENT] && IsFeatureReady.storageManagement();

    hasSalesAndPromotionsPermission = () => PERMISSIONS.hasPermissions([PERMISSIONS.SALES_AND_PROMOTIONS], this.context.permissions);

    hasCompany = () => this.context.companyName !== null;

    subscriptionNextPaymentDateFormat = (subscriptionEndDate) => {
        const date = new Date(subscriptionEndDate);
        date.setDate(subscriptionEndDate.getDate() + 1);

        return new Intl.DateTimeFormat("default", {
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(date);
    };

    getCompanyDetails = (isUpdate = false) => {
        this.setState(state => ({
            fetching: state.fetching + 1
        }));

        axios.get(`${API_URL}/company`, {
            headers: {
                Authorization: "Bearer " + this.context.token
            }
        }).then(res => {
            const sub = res.data.currentSubscription ?? null;

            this.context.appFns.updateUserContextProperties({
                [UserKeys.COUNTRY_CODE]: res.data.countryCode,
                [UserKeys.COMPANY_ABN]: res.data.abn,
            });

            // No current subscription (new account/demo/expired schema)
            const inDemoMode = res.data.mode === SubscriptionMode.DEMO;
            if (!sub) {
                const subscriptionStatus = inDemoMode ?
                    SubscriptionStatus.ACTIVE :
                    SubscriptionStatus.EXPIRED;
                this.setState(s => ({
                    fetching: s.fetching - 1,
                    companyNumber: res.data.companyNumber,
                    website: res.data.website,

                    subscriptionStart: null,
                    subscriptionEnd: null,
                    subscriptionNextPaymentDate: null,
                    subscriptionDurationUnit: null,
                    subscriptionPeriod: null,
                    subscriptionPaymentPeriod: null,
                    subscriptionTitle: "No Subscription",
                    subscriptionStatus,
                    hasActiveSubscription: false,
                    inDemoMode,
                    totalNumber: res.data.totalNumber,
                    employees: res.data.employees,
                    clients: res.data.clients
                }));
                return;
            }

            const startISO = sub.startAt;
            const periodNumber = sub.durationNumber;
            const periodUnit = String(sub.durationUnit || "").toLowerCase();
            const totalLicenses = sub.userCap ?? res.data.totalNumber;
            let subscriptionStatus = SubscriptionStatus.ACTIVE;
            if (res.data.isCancelled) {
                subscriptionStatus = SubscriptionStatus.REACTIVATE;
            } else if (sub.isExpired) {
                subscriptionStatus = SubscriptionStatus.EXPIRED;
            } else if (sub.periodsDue > 0) {
                subscriptionStatus = SubscriptionStatus.OVERDUE;
            }

            // Format start date
            const startDate = new Date(startISO);
            const subscriptionStart = new Intl.DateTimeFormat("default", {
                year: "numeric", month: "long", day: "numeric"
            }).format(startDate);

            // Compute end date
            const subscriptionEndDate = getCompanySubscriptionEnd(sub, false);

            const subscriptionEnd = new Intl.DateTimeFormat("default", {
                year: "numeric", month: "long", day: "numeric"
            }).format(subscriptionEndDate);

            const subscriptionNextPaymentDate = this.subscriptionNextPaymentDateFormat(subscriptionEndDate);

            const subscriptionTitle = planLabelFrom(sub);

            this.setState(state => ({
                //Setting Template
                fetching: state.fetching - 1,
                companyNumber: res.data.companyNumber,

                //MyBusiness
                website: res.data.website,

                subscriptionStart,
                subscriptionEnd,
                subscriptionNextPaymentDate,
                subscriptionPeriod: periodNumber,
                subscriptionDurationUnit: periodUnit,
                subscriptionPaymentPeriod: sub.paymentPeriod ?? null,
                subscriptionTitle,
                subscriptionStatus,
                hasActiveSubscription: !sub.isExpired,
                inDemoMode,
                totalNumber: totalLicenses,
                employees: res.data.employees,
                clients: res.data.clients,
                emailPro: res.data.emailPro ?? null,
            }));
        }).catch(error => {
            //remove loading for newly signup company, we use `hasCompany()` for this scenario
            if (error.getApiErrorDetails().message === "No company registration has been found") {
                this.setState(state => ({
                    fetching: state.fetching - 1,
                }));
            }
            Toast.error(error.getApiErrorDetails().message);
        });
    };

    getCompanyClientLicense = async () => {
        this.setState(state => ({
            fetching: state.fetching + 1
        }));

        return await axios.get(`${API_URL}/company/clientLicense`, {
            headers: {
                Authorization: "Bearer " + this.context.token
            }
        }).then(res => {
            this.props.setEnabledClientLicenseFlag(true);
        }).catch(error => {
            this.props.setEnabledClientLicenseFlag(false);
        }).finally(() => {
            this.setState(state => ({
                fetching: state.fetching - 1,
            }));
        });
    };

    getCountryCallingCode = () => {
        this.setState(state => ({
            fetching: state.fetching + 1
        }));

        // Getting the country code of the user.
        axios.get(`${API_URL}/callingCode`, {
            headers: {
                Authorization: "Bearer " + this.context.token
            }
        }).then(res => {
            let callingCode = res.data.callingCode;

            this.setState({
                countryCallingCode: callingCode,
            });
        }).finally(() => {
            this.setState(state => ({
                fetching: state.fetching - 1,
            }));
        }).catch(error => {
            console.log(error);
            Toast.error("An error occurred fetching from the server.");
        });
    };

    setForm = (form) => {
        this.setState({ currentForm: form });
    };

    setWebsite = (website) => {
        this.setState({ website: website });
    };

    setSubscriptionStatus = (status) => {
        this.setState({ subscriptionStatus: status });
    };

    render() {
        const {
            fetching, countryCallingCode, companyNumber, website,
            subscriptionStart, subscriptionEnd, subscriptionNextPaymentDate, subscriptionPeriod, subscriptionStatus,
            totalNumber, employees, clients
        } = this.state;

        const licenseTier = this.context[UserKeys.LICENSE_TIER];
        const hasLicenseTier = !!licenseTier;
        const hasRequiredDocuments = (this.context[UserKeys.REQUIRED_DOCUMENTS] || []).length > 0;
        const showRenewalAndEarnings = this.context.enabledClientLicense;

        const startingForm = this.isAccountOwner() ? Forms.MY_BUSINESS : Forms.SECURITY;

        let windowsPathname = window.location.pathname;
        if (windowsPathname.length > 0 && windowsPathname.charAt(windowsPathname.length - 1) === "/") {
            windowsPathname = windowsPathname.substring(0, windowsPathname.length - 1);
        }

        const pathname = windowsPathname === "/setting" ? startingForm : windowsPathname;
        return (
            <PortalPageShell maxWidth="100rem" paddingBottom="20px">
                <BusinessPortalHeader
                    title="Account Settings"
                    description="Manage business details, billing history, subscriptions, payment methods, security preferences, renewals, earnings, licenses, and privacy controls from one workspace."
                >
                    {companyNumber && (
                        <SettingsCustomerBadge>
                            <TextDiv fontWeight="800" fontSize="0.82rem" textAlign="inherit">Customer Number</TextDiv>
                            <TextDiv fontWeight="700" fontSize="1.25rem" textAlign="inherit">#{ companyNumber }</TextDiv>
                        </SettingsCustomerBadge>
                    )}
                </BusinessPortalHeader>

                { (pathname === null && fetching === 0) ?
                    <ErrorAlert>
                        <strong>The form failed to load successfully.</strong><br />
                        Please login again. If error failure persist please contact support.
                    </ErrorAlert>
                    :
                    <PortalWorkspace columns="17rem minmax(0, 1fr)" breakpoint="980px" gap="1rem" maxWidth="98.625rem">
                        <PortalSideNav>
                            <PortalSideNavHeader
                                kicker="Account"
                                title="Settings"
                                description="Choose a settings area to review or update."
                            />
                            <PortalSideNavList>
                                {this.isAccountOwner() && (
                                    <SettingsNavItem
                                        to={Forms.MY_BUSINESS}
                                        active={pathname === Forms.MY_BUSINESS}
                                        icon={MyBusinessIcon}
                                        label="My Business"
                                    />
                                )}
                                <SettingsNavItem
                                    to={Forms.SECURITY}
                                    active={pathname === Forms.SECURITY}
                                    icon={SecurityIcon}
                                    label="Security"
                                />
                                {(this.hasStorageManagementAccess() && this.hasCompany()) && (
                                    <SettingsNavItem
                                        to={Forms.STORAGE_MANAGEMENT}
                                        active={pathname === Forms.STORAGE_MANAGEMENT}
                                        icon={StorageManagementIcon}
                                        label="Storage Management"
                                        darkInactive
                                    />
                                )}
                                {(this.hasBillingPermission() && this.hasCompany()) && (
                                    <>
                                        <SettingsNavItem
                                            to={Forms.BILLING_HISTORY}
                                            active={pathname === Forms.BILLING_HISTORY}
                                            icon={BillingHistoryIcon}
                                            label="Billing History"
                                        />
                                        <SettingsNavItem
                                            to={Forms.SUBSCRIPTION}
                                            active={pathname === Forms.SUBSCRIPTION}
                                            icon={SubscriptionIcon}
                                            label="Subscription"
                                        />
                                        <SettingsNavItem
                                            to={Forms.PAYMENTS}
                                            active={pathname === Forms.PAYMENTS}
                                            icon={PaymentMethodsIcon}
                                            label="Payment Methods"
                                        />
                                        {showRenewalAndEarnings && (
                                            <SettingsNavItem
                                                to={Forms.RENEWALS_AND_EARNINGS}
                                                active={pathname === Forms.RENEWALS_AND_EARNINGS}
                                                icon={RenewalsAndEarningIcon}
                                                label="Renewals & Earnings"
                                            />
                                        )}
                                    </>
                                )}
                                {(hasLicenseTier || hasRequiredDocuments) && (
                                    <SettingsNavItem
                                        to={Forms.MY_LICENSE}
                                        active={pathname === Forms.MY_LICENSE}
                                        icon={MyLicenseIcon}
                                        label={hasLicenseTier ? "My License" : "My Documents"}
                                    />
                                )}
                                <SettingsNavItem
                                    to={Forms.PRIVACY_AND_DATA}
                                    active={pathname === Forms.PRIVACY_AND_DATA}
                                    icon={DataPrivacy}
                                    label="Privacy & Data"
                                />
                            </PortalSideNavList>
                        </PortalSideNav>
                        {
                            <SettingContext.Provider value={{

                                //MyBusiness
                                website: website,
                                setWebsite: this.setWebsite,

                                //CancelSubscription
                                subscriptionStart: subscriptionStart,
                                subscriptionEnd: subscriptionEnd,
                                subscriptionNextPaymentDate: subscriptionNextPaymentDate,
                                subscriptionPeriod: subscriptionPeriod,
                                subscriptionDurationUnit: this.state.subscriptionDurationUnit,
                                subscriptionPaymentPeriod: this.state.subscriptionPaymentPeriod,
                                subscriptionStatus: subscriptionStatus,
                                hasActiveSubscription: this.state.hasActiveSubscription,
                                inDemoMode: this.state.inDemoMode,
                                setSubscriptionStatus: this.setSubscriptionStatus,
                                totalNumber: totalNumber,
                                employees: employees,
                                clients: clients,
                                getCompanyDetails: this.getCompanyDetails,
                                token: this.context.token,
                                companyName: this.context.companyName,
                                email: this.context.email,
                                abn: this.context.abn,
                                setForm: this.setForm,
                                isAccountOwner: this.isAccountOwner(),
                                hasBillingPermission: this.hasBillingPermission(),
                                hasStorageManagementAccess: this.hasStorageManagementAccess(),
                                hasCompany: this.hasCompany,
                                countryCallingCode: countryCallingCode,
                                subscriptionTitle: this.state.subscriptionTitle,
                                emailPro: this.state.emailPro,
                            }}>
                                <SettingsContentPanel
                                    $loading={fetching > 0}
                                    $compact={[
                                        Forms.MY_BUSINESS,
                                        Forms.SECURITY,
                                        Forms.PAYMENTS
                                    ].includes(pathname)}
                                >
                                    { fetching > 0 && <LottieLoadingIndicator /> }
                                    { fetching === 0 &&
                                        <Switch>
                                            {pathname === Forms.RENEWALS_AND_EARNINGS && showRenewalAndEarnings && <Route path={Forms.RENEWALS_AND_EARNINGS} component={RenewalAndEarning} />}
                                            {pathname === Forms.BILLING_HISTORY && <Route path={Forms.BILLING_HISTORY} component={BillingHistory} />}

                                            {pathname === Forms.PAYMENTS && <Route path={Forms.PAYMENTS} component={Payments} />}
                                            {pathname === Forms.SECURITY && <Route path={Forms.SECURITY} component={Security} />}
                                            {(pathname === Forms.STORAGE_MANAGEMENT && this.hasStorageManagementAccess() && this.hasCompany()) && <Route path={Forms.STORAGE_MANAGEMENT} component={StorageManagement} />}
                                            {pathname === Forms.SUBSCRIPTION && <Route path={Forms.SUBSCRIPTION} component={CancelSubscription} />}
                                            {(this.isAccountOwner() && pathname === Forms.MY_BUSINESS) && <Route path={Forms.MY_BUSINESS} component={MyBusiness} />}
                                            {pathname === Forms.MY_LICENSE && <Route path={Forms.MY_LICENSE} component={MyLicense} />}
                                            {pathname === Forms.PRIVACY_AND_DATA && <Route path={Forms.PRIVACY_AND_DATA} component={PrivacyAndData} />}
                                            {<Route path="/setting" component={startingForm === Forms.MY_BUSINESS ? MyBusiness : Security} />}
                                        </Switch>
                                    }
                                </SettingsContentPanel>
                            </SettingContext.Provider>
                        }
                    </PortalWorkspace>
                }

            </PortalPageShell>
        );
    }
}

export default Setting;
